/**
 * CO POKAZAĆ NA LIŚCIE KOPII I W JAKIEJ KOLEJNOŚCI — dwie decyzje widoku, obie zgłoszone.
 */

/**
 * ILE DNI ZOSTAJE NA EKRANIE WIERSZ KOPII, KTÓREJ PLIKU JUŻ NIE MA.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  ⚠️ ZGŁOSZONE (28.08.2026): „te info o usuniętych kopiach też musi znikać po jakimś czasie, bo
 *  zaraz będziemy tu mieć straszny śmietnik". Wierszy bez pliku powstaje WIĘCEJ niż kopii: każda
 *  nowa kopia przycina najstarszą do limitu pięciu, a do tego dochodzą skasowania ręczne. Po
 *  miesiącu pracy lista „Zrobione kopie" składałaby się głównie z nieboszczyków.
 *
 *  ⚠️ NIE KASUJEMY WIERSZA, TYLKO PRZESTAJEMY GO POKAZYWAĆ W TYM MIEJSCU — i to jest różnica,
 *  która chroni decyzję zapisaną w API (`usunKopie`): „zadanie bez pliku nadal mówi, kto i kiedy
 *  zrobił kopię, a to bywa jedyną odpowiedzią na «co się wtedy działo»". Wpis zostaje w bazie,
 *  powstanie kopii siedzi w audycie (`machine-task.zlecone`), a od 28.08.2026 jej skasowanie też
 *  (`machine-task.kopia-usunieta`).
 *
 *  ⚠️ SIEDEM DNI, A NIE ZERO: wiersz „plik usunięty ręcznie z portalu" jest POTWIERDZENIEM, że
 *  kliknięcie zadziałało. Zniknięcie od razu po skasowaniu wyglądałoby jak zniknięcie CAŁEJ kopii
 *  i kazałoby się zastanawiać, czy nie skasowało się za dużo.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */
export const DNI_BEZ_PLIKU_NA_EKRANIE = 7;

export type WierszKopii = {
  status: string;
  /** `null` = pliku nie ma już w magazynie (przycięty do limitu albo skasowany ręcznie). */
  output: unknown;
  finishedAt: string | null;
  createdAt: string;
};

/**
 * Czy ten wiersz ma się pokazać na liście kopii.
 *
 * ⚠️ ZADANIE NIEZAKOŃCZONE I NIEUDANE POKAZUJEMY ZAWSZE, niezależnie od wieku — pierwsze dlatego,
 * że właśnie trwa, drugie dlatego, że nieudana kopia jest informacją do działania, a nie historią.
 * Reguła dotyczy WYŁĄCZNIE zadań zakończonych powodzeniem, po których nie został plik.
 */
export function czyPokazacKopie(z: WierszKopii, teraz: Date): boolean {
  if (z.status !== 'DONE') return true;
  if (z.output !== null && z.output !== undefined) return true;
  const kiedy = Date.parse(z.finishedAt ?? z.createdAt);
  /** ⚠️ NIECZYTELNA DATA ZOSTAJE WIDOCZNA. Ukrycie wiersza, o którym nie wiemy nic, byłoby zgadywaniem. */
  if (Number.isNaN(kiedy)) return true;
  return teraz.getTime() - kiedy < DNI_BEZ_PLIKU_NA_EKRANIE * 24 * 60 * 60 * 1000;
}

/**
 * KOLEJNOŚĆ NAZW WĘZŁÓW NA LIŚCIE WYBORU.
 *
 * ⚠️ ZGŁOSZONE (28.08.2026): „w kopiach zapasowych lista nie jest alfabetyczna". Szła kolejnością
 * z API (czyli kolejnością rejestracji agentów), więc na PESA wychodziło
 * „PESA-3 · chmura-mssql-pesa · PESA-1 · PESA-2".
 *
 * ⚠️ `numeric: true` JEST TU ISTOTNE, nie ozdobne: bez niego „PESA-10" staje PRZED „PESA-2",
 * bo porównanie idzie znak po znaku. Wysp z dziesięcioma automatami jeszcze nie mamy, ale to
 * dokładnie ten rodzaj sortowania, który psuje się cicho i późno.
 *
 * ⚠️ KOLACJA POLSKA, bo nazwy automatów bywają polskie („Hala A — wejście", „Łódź"), a domyślne
 * porównanie stawia „Ł" po „Z".
 */
export function porownajNazwyWezlow(a: string, b: string): number {
  return a.localeCompare(b, 'pl', { numeric: true, sensitivity: 'base' });
}
